import React, { useState, useEffect, useCallback, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import Header from '../Header/Header';
import DocMenu from '../DocMenu';
import NumDoss from '../NumDoss';
import ListTable from './FactureTable/ListTable';
import GenPdf from '../GenPdf';
import { genFacTablePDF, getFacTablePDF, getCodeFacture } from './Utils';
import { numberToFormat, handleChangeDateToMonth } from "../Utils";
import { fetchAllFac } from '../../redux/actions/facActions';
import { fetchAllReminderByDoss } from "../../redux/actions/reminderActions";
import { changeTitleName, changePdfFileName } from "../../redux/actions/emailAction";
import "../../Styles/Factures/FactureTable.scss";

const FactureTable = () => {
  const dispatch = useDispatch();
  const factures = useSelector(state => state.facture.allfacture);
  const dossierValue = useSelector(state => state.devis.dossierValue);
  const reminders = useSelector(state => state.reminder.items);

  const [facturesData, setFacturesData] = useState([]);
  const [totalHt, setTotalHt] = useState(0);
  const [totalTva, setTotalTva] = useState(0);
  const [totalTtc, setTotalTtc] = useState(0);
  const [openFile, setOpenFile] = useState(false);
  const [disableButton, setDisableButton] = useState(false);
  const [file, setFile] = useState(null);

  // Permet de distinguer le premier rendu des mises à jour suivantes
  const isMounted = useRef(false);

  // ─── handleGetReminder ────────────────────────────────────────────────────
  const handleGetReminder = useCallback((validation, date, days, numclient) => {
    const reminderSent = reminders.filter(
      r => r.numclient === numclient &&
        (days === 15 ? r.first_reminder == 1 : r.first_reminder == 0)
    );
    const reminderDate = reminderSent.length > 0
      ? new Date(reminderSent[0].date_reminder).toLocaleDateString('en-CA')
      : '';

    const today = new Date();
    const dateFormated = new Date(date);
    dateFormated.setDate(dateFormated.getDate() + days);

    return reminderSent.length > 0 && today >= dateFormated && validation != 1
      ? reminderDate !== '' ? reminderDate : 'À ENVOYER'
      : '';
  }, [reminders]);

  // ─── loadData ─────────────────────────────────────────────────────────────
  const loadData = useCallback((updateData = false, sourceData = null) => {
    dispatch(changePdfFileName(""));

    if (!updateData) {
      if (!dossierValue) return;

      const newFacturesData = factures
        .filter(item => item.ndossier === dossierValue)
        .map(item => ({
          ...item,
          // ✅ FIX 1 : parse uniquement si c'est encore une string
          dossier: typeof item.dossier === 'string'
            ? JSON.parse(item.dossier)
            : item.dossier,
          montant_tva: parseFloat((item.total_ht * (item.tva / 100)).toFixed(2)),
          amount_ca: item.bank === 'ca'
            ? numberToFormat(item.total_ht + item.total_ht * (item.tva / 100)) : "",
          amount_bred: item.bank !== 'ca'
            ? numberToFormat(item.total_ht + item.total_ht * (item.tva / 100)) : "",
          month_facture: {
            month: handleChangeDateToMonth(item.date_ech),
            ref: getCodeFacture(item.numclient)
          },
          date_ech: new Date(item.date_ech).toLocaleDateString('en-CA'),
          payment_date: item.payment_date !== null
            ? new Date(item.payment_date).toLocaleDateString('en-CA') : null,
          contrat_valide: item.contrat_valide,
          first_reminder: handleGetReminder(item.validation, item.date_ech, 15, item.numclient),
          second_reminder: handleGetReminder(item.validation, item.date_ech, 30, item.numclient),
        }));

      const ht = newFacturesData.reduce((acc, cur) => acc + cur.total_ht, 0);
      const tva = newFacturesData.reduce((acc, cur) => acc + cur.montant_tva, 0);

      setTotalHt(numberToFormat(ht));
      setTotalTva(numberToFormat(tva));
      setTotalTtc(numberToFormat(ht + tva));
      setFacturesData(newFacturesData);

    } else {
      // updateData = true → on retravaille les données déjà en state (passées en param)
      const base = (sourceData || []).filter(item => item.ndossier === dossierValue);
      const newFacturesData = base.map(item => ({
        ...item,
        montant_tva: parseFloat((item.total_ht * (item.tva / 100)).toFixed(2)),
        amount_ca: item.bank === 'ca'
          ? numberToFormat(item.total_ht + item.total_ht * (item.tva / 100)) : "",
        amount_bred: item.bank !== 'ca'
          ? numberToFormat(item.total_ht + item.total_ht * (item.tva / 100)) : "",
        month_facture: {
          month: handleChangeDateToMonth(item.date_ech),
          ref: getCodeFacture(item.numclient)
        },
        date_ech: new Date(item.date_ech).toLocaleDateString('en-CA'),
        contrat_valide: item.contrat_valide,
        first_reminder: handleGetReminder(item.validation, item.date_ech, 15, item.numclient),
        second_reminder: handleGetReminder(item.validation, item.date_ech, 30, item.numclient),
      }));
      setFacturesData(newFacturesData);
    }

    setFile(null);
  }, [dispatch, factures, dossierValue, handleGetReminder]);

  // ─── componentDidMount ────────────────────────────────────────────────────
  useEffect(() => {
    dispatch(changePdfFileName(""));
    if (dossierValue) dispatch(fetchAllReminderByDoss(dossierValue));
    if (factures.length > 0) loadData();
    else dispatch(fetchAllFac());
    isMounted.current = true;
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // ─── componentDidUpdate (factures / dossierValue) ─────────────────────────
  useEffect(() => {
    if (!isMounted.current) return; // ignore le premier rendu (géré dans le mount)
    dispatch(fetchAllReminderByDoss(dossierValue));
    loadData();
  }, [factures, dossierValue]); // eslint-disable-line react-hooks/exhaustive-deps

  // ─── handleChange ─────────────────────────────────────────────────────────
  const handleChange = useCallback((event, index) => {
    setFacturesData(prev => {
      let updated = [...prev];
      const { name, value } = event.target;

      if (name === "contrat_valide") {
        const contratStatus = updated[index].contrat_valide == 0 ? 1 : 0;
        // ✅ FIX 2 : map retourne bien un nouvel objet pour chaque item
        updated = updated.map(item => ({ ...item, contrat_valide: contratStatus }));
      } else if (name === "validation") {
        updated[index] = {
          ...updated[index],
          payment_date: updated[index].validation == 0 ? updated[index].payment_date : null,
          validation: updated[index].validation == 0 ? 1 : 0,
        };
      } else if (name === "payment_date") {
        updated[index] = { ...updated[index], payment_date: value, validation: 1 };
      }

      // recalcul immédiat sur les données mises à jour
      loadData(true, updated);
      return updated;
    });
  }, [loadData]);

  // ─── handleUpdateClick ────────────────────────────────────────────────────
  const handleUpdateClick = useCallback(async () => {
    const json = { data: facturesData, ndossier: dossierValue };
    await fetch("/api/facture/updatefacture", {
      method: "POST",
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
      body: JSON.stringify(json),
    }).catch(err => console.log(err));

    await dispatch(fetchAllFac());
  }, [dispatch, facturesData, dossierValue]);

  // ─── handleGenClick ───────────────────────────────────────────────────────
  const handleGenClick = useCallback(async () => {
    handleUpdateClick();
    setFile(null);
    setDisableButton(true);

    const json = {
      factures: facturesData,
      ndossier: dossierValue,
      total_ht: totalHt,
      total_tva: totalTva,
      total_ttc: totalTtc,
      date: new Date().toLocaleDateString('en-CA'),
    };
    const blob = await genFacTablePDF({
      method: "POST",
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
      body: JSON.stringify(json),
    });

    if (blob) {
      setFile(blob);
      setDisableButton(false);
      setOpenFile(true);
    }
  }, [handleUpdateClick, facturesData, dossierValue, totalHt, totalTva, totalTtc]);

  // ─── handleShowPdf ────────────────────────────────────────────────────────
  const handleShowPdf = useCallback(async () => {
    setFile(null);
    setDisableButton(true);
    const response = await getFacTablePDF(dossierValue, 'pdf');

    if (response.blob.size > 100) {
      setFile(response.blob);
      setOpenFile(true);
      dispatch(changePdfFileName(response.fileName));
    }
    setDisableButton(false);
  }, [dispatch, dossierValue]);

  // ─── handleGetPdfFile ─────────────────────────────────────────────────────
  const handleGetPdfFile = useCallback(async () => {
    setFile(null);
    const response = await getFacTablePDF(dossierValue, 'pdf');

    if (response.blob.size > 100) {
      setFile(response.blob);
      dispatch(changePdfFileName(response.fileName));
    }
  }, [dispatch, dossierValue]);

  // ─── handleGetWordFile ────────────────────────────────────────────────────
  const handleGetWordFile = useCallback(async () => {
    setFile(null);
    const response = await getFacTablePDF(dossierValue, 'word');
    const wordType = 'application/vnd.openxmlformats-officedocument.wordprocessingml.document';

    if (response.blob.type === wordType) {
      setFile(response.blob);
      dispatch(changePdfFileName(response.fileName));
    }
  }, [dispatch, dossierValue]);

  // ─── closeDoc ─────────────────────────────────────────────────────────────
  const closeDoc = useCallback(() => {
    setOpenFile(false);
    setFile(null);
  }, []);

  // ─── handleChangeTitleName ────────────────────────────────────────────────
  const handleChangeTitleName = useCallback(() => {
    dispatch(changeTitleName(`Tableau de Facturation ${dossierValue}`));
  }, [dispatch, dossierValue]);

  // ─── render ───────────────────────────────────────────────────────────────
  return (
    <div className="facture-table">
      <Header />
      <DocMenu
        file={file}
        getSourceFile={handleGetWordFile}
        getPDF={handleGetPdfFile}
        handleChangeTitleName={handleChangeTitleName}
      />
      <h1 className="title">Tableau de Facturation</h1>
      <NumDoss />
      <ListTable
        factures={factures}
        facturesData={facturesData}
        totalHt={totalHt}
        totalTva={totalTva}
        totalTtc={totalTtc}
        disableButton={disableButton}
        loadData={loadData}
        handleChange={handleChange}
        handleUpdateClick={handleUpdateClick}
        handleGenClick={handleGenClick}
        handleShowPdf={handleShowPdf}
      />
      <GenPdf
        file={file}
        isOpen={openFile}
        closeDoc={closeDoc}
        scale={file !== null ? 1 : 0.5}
      />
    </div>
  );
};

export default FactureTable;